import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MagnifyingGlass, Plus, Trash, HandArrowUp, Package, Receipt } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function fmt(iso) {
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function TakeOut() {
  const [items, setItems] = useState([]);
  const [issues, setIssues] = useState([]);
  const [q, setQ] = useState("");
  const [lines, setLines] = useState([]);
  const [takenBy, setTakenBy] = useState("");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [inv, iss] = await Promise.all([api.get("/inventory"), api.get("/issues", { params: { limit: 20 } })]);
    setItems(inv.data); setIssues(iss.data);
  };
  useEffect(() => { load(); }, []);

  const results = useMemo(() => {
    if (!q.trim()) return [];
    const s = q.toLowerCase();
    return items.filter((it) => it.quantity > 0 && !lines.some((l) => l.item_id === it.id) &&
      `${it.name} ${it.sku} ${it.category}`.toLowerCase().includes(s)).slice(0, 6);
  }, [q, items, lines]);

  const addLine = (it) => { setLines((l) => [...l, { item_id: it.id, name: it.name, unit: it.unit, available: it.quantity, image_path: it.image_path, quantity: 1 }]); setQ(""); };
  const setQty = (id, v) => setLines((l) => l.map((x) => (x.item_id === id ? { ...x, quantity: v } : x)));
  const removeLine = (id) => setLines((l) => l.filter((x) => x.item_id !== id));

  const submit = async () => {
    if (!takenBy.trim()) { toast.error("Enter who is taking the items"); return; }
    if (lines.length === 0) { toast.error("Add at least one item"); return; }
    for (const l of lines) {
      const n = Number(l.quantity);
      if (!n || n <= 0) { toast.error(`Enter a quantity for ${l.name}`); return; }
      if (n > l.available) { toast.error(`Only ${l.available} ${l.unit} of ${l.name} available`); return; }
    }
    setBusy(true);
    try {
      const { data } = await api.post("/issues", { taken_by: takenBy.trim(), reference, note, lines: lines.map((l) => ({ item_id: l.item_id, quantity: Number(l.quantity) })) });
      toast.success(`Slip ${data.slip_no} recorded — ${data.lines.length} item(s) taken out`);
      setLines([]); setTakenBy(""); setReference(""); setNote("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="take-out-page">
      <PageHeader title="Take from store" subtitle="Issue one or more items to a staff member, patient or department in a single slip" />
      <div className="p-6 md:p-8 grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-5">
          <div className="bg-card border border-border rounded-md p-5 space-y-4">
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="take-out-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search item to take out…" className="pl-9" />
              {results.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-card border border-border rounded-md shadow-md overflow-hidden" data-testid="take-out-results">
                  {results.map((it) => (
                    <button key={it.id} type="button" data-testid={`take-out-result-${it.id}`} onClick={() => addLine(it)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm hover:bg-accent transition-colors duration-150">
                      {it.image_path ? <img src={`${API}/files/${it.image_path}`} alt="" className="h-8 w-8 rounded object-cover border border-border" />
                        : <span className="h-8 w-8 rounded bg-muted flex items-center justify-center"><Package size={15} className="text-muted-foreground" /></span>}
                      <span className="flex-1 min-w-0"><span className="font-medium block truncate">{it.name}</span><span className="text-xs text-muted-foreground">{it.category}{it.sku ? ` · ${it.sku}` : ""}</span></span>
                      <span className="text-xs text-muted-foreground tnums">{it.quantity} {it.unit}</span>
                      <Plus size={15} className="text-primary" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {lines.length === 0 ? (
              <div className="h-32 flex flex-col items-center justify-center text-muted-foreground text-sm gap-2 border border-dashed border-border rounded-md">
                <HandArrowUp size={22} /> Search and add the items being taken out
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="take-out-lines">
                <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr><th className="py-2">Item</th><th className="py-2 text-right">Available</th><th className="py-2 text-right w-28">Qty</th><th className="w-10" /></tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {lines.map((l) => (
                    <tr key={l.item_id} data-testid={`take-out-line-${l.item_id}`}>
                      <td className="py-2.5 font-medium">{l.name}</td>
                      <td className="py-2.5 text-right text-muted-foreground tnums">{l.available} {l.unit}</td>
                      <td className="py-2.5 text-right">
                        <Input type="number" min="0.01" step="any" max={l.available} data-testid={`take-out-qty-${l.item_id}`} value={l.quantity}
                          onChange={(e) => setQty(l.item_id, e.target.value)} className="h-8 text-right" />
                      </td>
                      <td className="py-2.5 text-right">
                        <Button size="icon" variant="ghost" data-testid={`take-out-remove-${l.item_id}`} onClick={() => removeLine(l.item_id)} className="h-8 w-8 hover:text-destructive"><Trash size={15} /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="bg-card border border-border rounded-md p-5 grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Taken by</Label>
              <Input data-testid="take-out-taken-by" value={takenBy} onChange={(e) => setTakenBy(e.target.value)} placeholder="Staff name / department" />
            </div>
            <div className="space-y-1.5">
              <Label>Purpose / patient <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input data-testid="take-out-reference" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. Patient #1042 fitting" />
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label>Note <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Textarea data-testid="take-out-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
            </div>
            <div className="sm:col-span-2 flex justify-end">
              <Button data-testid="take-out-submit" onClick={submit} disabled={busy || lines.length === 0} className="bg-primary hover:bg-primary/90 gap-2">
                <HandArrowUp size={17} weight="bold" /> {busy ? "Recording…" : `Take out ${lines.length || ""} item${lines.length === 1 ? "" : "s"}`}
              </Button>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="bg-card border border-border rounded-md">
            <div className="px-5 py-4 border-b border-border flex items-center gap-2"><Receipt size={18} className="text-primary" /><h3 className="font-heading font-semibold">Recent slips</h3></div>
            {issues.length === 0 ? <p className="p-5 text-sm text-muted-foreground">No items taken out yet.</p> : (
              <ul className="divide-y divide-border" data-testid="recent-slips">
                {issues.map((s) => (
                  <li key={s.id} data-testid={`slip-${s.id}`} className="px-5 py-3.5">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-medium">{s.taken_by} <span className="text-xs text-muted-foreground font-normal">· {s.slip_no}</span></p>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">{fmt(s.created_at)}</span>
                    </div>
                    {s.reference && <p className="text-xs text-muted-foreground mt-0.5">{s.reference}</p>}
                    <p className="text-xs text-muted-foreground mt-1.5">{s.lines.map((l) => `${l.item_name} × ${l.quantity} ${l.unit}`).join(", ")}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
